import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Chip,
  Stack,
  Divider,
  Modal,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ViewList as ListViewIcon,
  Timeline as TimelineViewIcon
} from '@mui/icons-material';
import {
  useGetItemQuery,
  useGetItemsQuery,
  useGetConnectedQuery,
  useUpdatePropertyMutation,
  useCreateItemMutation,
  useDeleteItemMutation
} from '../store/docApi';
import { formatDateShort, isToday, getTimeShort, getDayMonth } from '../utils';
import { useParams } from 'react-router-dom';
import TripTimeline from './TripTimeline';
import Links from '../components/widgets/Links';
import getStepIcon from './StepIcon';
import StepActions from './StepActions';
import StepContent from './StepContent';

export function groupStepsByDate(steps) {
  const groupedItems = {};
  let sortedSteps = steps.map((step, i) => ({ ...step, index: i })).sort((p1, p2) => new Date(p1.startDate) - new Date(p2.startDate));
  sortedSteps.forEach((step) => {
    const date = getDayMonth(new Date(step.startDate));
    groupedItems[date] = groupedItems[date] ? [...groupedItems[date], step] : [step];
  });
  return groupedItems;
}

function TripDetails() {
  let { pk, id } = useParams();
  let _id = `trip/${pk}/${id}`;
  const { data: trip, isLoading } = useGetItemQuery({ label: 'trip', id: id });
  const { data: notes, refetch: refetchNotes } = useGetConnectedQuery({ _id: _id, container: 'content', pk: 'usr' });
  const { data: allQuests = [] } = useGetItemsQuery({ label: 'quest' });
  const [stepsByDay, setStepsByDay] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stepForm, setStepForm] = useState({
    name: '',
    type: '',
    pictures: [],
    startDate: '',
    endDate: '',
    description: '',
    host: '',
    links: [],
    questId: '',
    questStepId: ''
  });
  const [selectedQuestId, setSelectedQuestId] = useState('');
  const [selectedQuestStep, setSelectedQuestStep] = useState(null);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [updateProperty] = useUpdatePropertyMutation();
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [addNoteStep, setAddNoteStep] = useState(null);
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [noteForm, setNoteForm] = useState({ note: '', startDate: '', status: 'interested' });
  const [createItem] = useCreateItemMutation();
  const [deleteNote] = useDeleteItemMutation();
  const [editingNote, setEditingNote] = useState(null);
  const [deleteNoteInfo, setDeleteNoteInfo] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  useEffect(() => {
    if (trip?.steps) setStepsByDay(groupStepsByDate(trip.steps));
  }, [trip]);

  const getConnectedNotes = (step) => {
    if (!notes) return null;
    const stepNotes = notes.filter((note) => note.connections?.some((conn) => conn._step_id === step.id));
    if (!stepNotes.length) return null;
    return (
      <Box sx={{ mt: 1 }}>
        {stepNotes.map((note, i) => (
          <Card key={i} variant="outlined" size="sm" sx={{ mb: 1, width: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography level="body-sm">{note.note}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <IconButton size="sm" variant="plain" color="neutral" onClick={() => handleOpenNoteModal(step, note)} title="Edit Note">
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => setDeleteNoteInfo({ pk: note.pk, id: note.id })}
                    title="Delete Note"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              {note.pictures?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {note.pictures.map((pic, j) => (
                    <img src={pic} key={j} alt={`Note ${i + 1}`} height={200} />
                  ))}
                </Box>
              )}
            </CardContent>
          </Card>
        ))}
      </Box>
    );
  };

  const handleOpenModal = (step = null, index = null) => {
    if (step) {
      const { ...rest } = step;
      setStepForm({ ...rest });
      setSelectedQuestId(step.questId || '');
      setSelectedQuestStep(null);
      setEditingStepIndex(index);
      if (step.questId && step.questStepId) {
        const quest = allQuests.find((q) => q.id === step.questId);
        const questStep = quest?.steps?.find((s) => s.id === step.questStepId);
        if (questStep) setSelectedQuestStep(questStep);
      }
    } else {
      const now = new Date();
      const pad = (n) => n.toString().padStart(2, '0');
      const formattedNow = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
      setStepForm({
        name: '',
        type: '',
        pictures: [],
        startDate: formattedNow,
        endDate: '',
        description: '',
        host: '',
        links: [],
        questId: '',
        questStepId: ''
      });
      setSelectedQuestId('');
      setSelectedQuestStep(null);
      setEditingStepIndex(null);
    }
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedQuestId('');
    setSelectedQuestStep(null);
    setEditingStepIndex(null);
  };
  const handleStepFormChange = (e) => {
    const { name, value } = e.target;
    setStepForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleStepTypeChange = (_, value) => {
    setStepForm((prev) => ({ ...prev, type: value }));
  };
  const handlePickQuest = (event) => {
    const value = event.target.value;
    const selectedQuest = allQuests.find((q) => q.id === value);
    setSelectedQuestId(value);
    setSelectedQuestStep(null);
    setStepForm((prev) => ({
      ...prev,
      questId: value,
      questStepId: '',
      type: value ? 'quest' : '',
      name: selectedQuest?.name || prev.name,
      description: selectedQuest?.description || prev.description,
      host: selectedQuest?.host || prev.host,
      links: selectedQuest?.links || prev.links,
      pictures: selectedQuest?.pictures || prev.pictures
    }));
  };
  const handlePickQuestStep = (stepId) => {
    const quest = allQuests.find((q) => q.id === selectedQuestId);
    const questStep = quest?.steps?.find((s) => s.id === stepId);
    setSelectedQuestStep(questStep);
    if (questStep) {
      setStepForm((prev) => ({
        ...prev,
        name: questStep.name || prev.name,
        type: questStep.type || prev.type,
        pictures: questStep.pictures || prev.pictures,
        description: questStep.description || prev.description,
        host: questStep.host || prev.host,
        links: questStep.links || prev.links,
        questId: selectedQuestId,
        questStepId: stepId
      }));
    }
  };
  const handleAddOrEditStep = async () => {
    if (!trip) return;
    let newSteps;
    if (editingStepIndex !== null) {
      // Edit mode
      newSteps = [...(trip.steps || [])];
      newSteps[editingStepIndex] = { ...stepForm, questId: selectedQuestId, questStepId: selectedQuestStep?.id || '' };
    } else {
      // Add mode
      const newStep = {
        ...stepForm,
        id: `${Date.now()}`,
        questId: selectedQuestId,
        questStepId: selectedQuestStep?.id || '',
        type: selectedQuestId ? 'quest' : stepForm.type,
        name: selectedQuestStep?.name || stepForm.name,
        description: selectedQuestStep?.description || stepForm.description,
        host: selectedQuestStep?.host || stepForm.host,
        links: selectedQuestStep?.links || stepForm.links,
        pictures: selectedQuestStep?.pictures || stepForm.pictures
      };
      newSteps = [...(trip.steps || []), newStep];
    }
    await updateProperty({ pk: trip.pk, id: trip.id, name: 'steps', value: newSteps });
    setIsModalOpen(false);
  };

  const handleDeleteStep = async (index) => {
    if (!trip) return;
    const newSteps = (trip.steps || []).filter((_, i) => i !== index);
    await updateProperty({ pk: trip.pk, id: trip.id, name: 'steps', value: newSteps });
    setDeleteConfirmIndex(null);
  };

  const handleOpenNoteModal = (step, note = null) => {
    const now = new Date();
    const pad = (n) => n.toString().padStart(2, '0');
    const formattedNow = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
    setAddNoteStep(step);
    if (note) {
      setNoteForm({
        note: note.note || '',
        startDate: note.startDate ? note.startDate.slice(0, 16) : formattedNow,
        status: note.connections?.find((c) => c._id?.startsWith('quest/'))?.progress || 'interested'
      });
      setEditingNote(note);
    } else {
      setNoteForm({ note: '', startDate: formattedNow, status: 'interested' });
      setEditingNote(null);
    }
    setNoteModalOpen(true);
  };
  const handleCloseNoteModal = () => {
    setNoteModalOpen(false);
    setAddNoteStep(null);
    setNoteForm({ note: '', startDate: '', status: 'interested' });
    setEditingNote(null);
  };
  const handleNoteFormChange = (e) => {
    const { name, value } = e.target;
    setNoteForm((prev) => ({ ...prev, [name]: value }));
  };
  const handleNoteStatusChange = (_, value) => {
    setNoteForm((prev) => ({ ...prev, status: value }));
  };
  const handleSaveNote = async () => {
    if (!addNoteStep) return;
    const connections = [
      {
        _id: `trip/${trip.pk}/${trip.id}`,
        _step_id: addNoteStep.id
      }
    ];
    if (addNoteStep.questId && addNoteStep.questStepId) {
      connections.push({
        _id: `quest/${addNoteStep.questId}`,
        _step_id: addNoteStep.questStepId,
        progress: noteForm.status
      });
    }
    if (editingNote) {
      // Edit mode: update note properties
      await updateProperty({ pk: editingNote.pk, id: editingNote.id, name: 'note', value: noteForm.note });
      await updateProperty({ pk: editingNote.pk, id: editingNote.id, name: 'startDate', value: noteForm.startDate });
      await updateProperty({ pk: editingNote.pk, id: editingNote.id, name: 'connections', value: connections });
    } else {
      // Add mode
      await createItem({
        data: {
          label: 'note',
          note: noteForm.note,
          startDate: noteForm.startDate,
          connections
        }
      });
    }
    if (refetchNotes) refetchNotes();
    setNoteModalOpen(false);
  };
  const handleDeleteNote = async () => {
    if (!deleteNoteInfo) return;
    await deleteNote({ pk: deleteNoteInfo.pk, id: deleteNoteInfo.id });
    if (refetchNotes) refetchNotes();
    setDeleteNoteInfo(null);
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (!trip) return <Typography color="error">Trip not found</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">{trip.name}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={(e, newMode) => newMode && setViewMode(newMode)} size="small">
            <ToggleButton value="list">
              <ListViewIcon />
            </ToggleButton>
            <ToggleButton value="timeline">
              <TimelineViewIcon />
            </ToggleButton>
          </ToggleButtonGroup>
          <Button startIcon={<AddIcon />} variant="outlined" onClick={() => handleOpenModal()}>
            Add Step
          </Button>
          <IconButton
            variant="outlined"
            onClick={() => {
              /* TODO: Edit trip handler */
            }}
          >
            <EditIcon />
          </IconButton>
        </Box>
      </Box>

      {trip.imageUrl && (
        <Box sx={{ width: '100%', mb: 2 }}>
          <img src={trip.imageUrl} alt={trip.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      )}

      <CardContent>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {trip.id}
        </Typography>
        {trip.startDate && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {formatDateShort(trip.startDate)}
            {trip.endDate && <> - {formatDateShort(trip.endDate)}</>}
          </Typography>
        )}
        {trip.organizer && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            Organized by {trip.organizer.name}
          </Typography>
        )}
        {trip.description && <Typography sx={{ mb: 1 }}>{trip.description}</Typography>}
        {trip.hashtags?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            {trip.hashtags.map((tag, i) => (
              <Chip key={i} size="small" label={tag} />
            ))}
          </Box>
        )}
        {trip.attendees?.length > 0 && <Typography variant="body2">{trip.attendees.map((obj) => obj.name).join(', ')}</Typography>}
        {trip.links?.length > 0 && (
          <Box sx={{ mt: 1 }}>
            <Links links={trip.links} />
          </Box>
        )}
      </CardContent>

      {viewMode === 'list' ? (
        Object.entries(stepsByDay).map(([day, steps], dayIndex) => (
          <Box key={day} sx={{ mb: 3 }}>
            <Typography variant="h5" sx={{ backgroundColor: 'background.paper', color: 'text.primary', p: 1, mb: 1 }}>
              {Object.keys(stepsByDay).length > 1 ? `Day ${dayIndex + 1} - ${day}` : day}
            </Typography>

            {steps.map((step, stepIndex) => {
              const globalIndex = trip.steps.findIndex((s) => s.id === step.id);
              return (
                <Card key={stepIndex} variant="outlined" sx={{ mb: 1, bgcolor: isToday(step.date) ? 'warning.light' : undefined }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ minWidth: 60, textAlign: 'right' }}>
                        <Typography variant="body2">{getTimeShort(step.startDate)}</Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getStepIcon(step.type, { sx: { color: '#7f9864', mr: 1 } })}
                          <Typography variant="h6" sx={{ flex: 1 }}>
                            {step.name}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {step.id}
                          </Typography>
                          <StepActions
                            onEdit={() => handleOpenModal(step, globalIndex)}
                            onDelete={() => setDeleteConfirmIndex(globalIndex)}
                            onAddNote={() => handleOpenNoteModal(step)}
                          />
                        </Box>
                      </Box>
                    </Box>
                    <StepContent step={step} getConnectedNotes={getConnectedNotes} />
                  </CardContent>
                </Card>
              );
            })}
          </Box>
        ))
      ) : (
        <TripTimeline
          steps={trip.steps || []}
          onEditStep={(step, index) => handleOpenModal(step, index)}
          onDeleteStep={setDeleteConfirmIndex}
          onAddNote={handleOpenNoteModal}
          getConnectedNotes={getConnectedNotes}
        />
      )}

      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <Dialog open={isModalOpen} onClose={handleCloseModal} maxWidth="sm" fullWidth>
          <DialogTitle>{editingStepIndex !== null ? 'Edit Step' : 'Add Step'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Pick Quest (optional)</InputLabel>
                <Select value={stepForm.questId || ''} onChange={handlePickQuest} label="Pick Quest (optional)">
                  <MenuItem value="">None</MenuItem>
                  {allQuests.map((q) => (
                    <MenuItem key={q.id} value={q.id}>
                      {q.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {stepForm.questId && (
                <FormControl fullWidth>
                  <InputLabel>Pick Step from Quest</InputLabel>
                  <Select
                    value={stepForm.questStepId || ''}
                    onChange={(e) => handlePickQuestStep(e.target.value)}
                    label="Pick Step from Quest"
                  >
                    <MenuItem value="">None</MenuItem>
                    {(() => {
                      const quest = allQuests.find((q) => q.id === stepForm.questId);
                      return (
                        quest?.steps?.map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name || `Step ${s.id}`}
                          </MenuItem>
                        )) || []
                      );
                    })()}
                  </Select>
                </FormControl>
              )}

              <TextField fullWidth label="Name" name="name" value={stepForm.name} onChange={handleStepFormChange} required />

              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select name="type" value={stepForm.type} onChange={handleStepTypeChange} label="Type">
                  <MenuItem value="quest">Quest</MenuItem>
                  <MenuItem value="lodging">Lodging</MenuItem>
                  <MenuItem value="car">Car</MenuItem>
                  <MenuItem value="flight">Flight</MenuItem>
                  <MenuItem value="ferry">Ferry</MenuItem>
                  <MenuItem value="nature">Nature</MenuItem>
                  <MenuItem value="tour">Tour</MenuItem>
                  <MenuItem value="sightseeing">Sightseeing</MenuItem>
                  <MenuItem value="meal">Meal</MenuItem>
                  <MenuItem value="drink">Drink</MenuItem>
                  <MenuItem value="meditation">Meditation</MenuItem>
                  <MenuItem value="">Other</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Description"
                name="description"
                value={stepForm.description}
                onChange={handleStepFormChange}
                multiline
                rows={2}
              />

              <TextField
                fullWidth
                label="Start Date"
                name="startDate"
                type="datetime-local"
                value={stepForm.startDate}
                onChange={handleStepFormChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                fullWidth
                label="End Date"
                name="endDate"
                type="datetime-local"
                value={stepForm.endDate}
                onChange={handleStepFormChange}
                InputLabelProps={{ shrink: true }}
              />

              <TextField fullWidth label="Host" name="host" value={stepForm.host} onChange={handleStepFormChange} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Cancel</Button>
            <Button variant="contained" onClick={handleAddOrEditStep}>
              {editingStepIndex !== null ? 'Save Changes' : 'Add Step'}
            </Button>
          </DialogActions>
        </Dialog>
      </Modal>

      <Dialog open={deleteConfirmIndex !== null} onClose={() => setDeleteConfirmIndex(null)}>
        <DialogTitle>Delete Step?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>Are you sure you want to delete this step? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmIndex(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => handleDeleteStep(deleteConfirmIndex)}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={noteModalOpen} onClose={handleCloseNoteModal} maxWidth="sm" fullWidth>
        <DialogTitle>Add Note</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Note"
              name="note"
              value={noteForm.note}
              onChange={handleNoteFormChange}
              multiline
              rows={3}
              required
            />

            <TextField
              fullWidth
              label="Date"
              name="startDate"
              type="datetime-local"
              value={noteForm.startDate}
              onChange={handleNoteFormChange}
              InputLabelProps={{ shrink: true }}
              required
            />

            {addNoteStep && addNoteStep.questId && addNoteStep.questStepId && (
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select name="status" value={noteForm.status} onChange={handleNoteStatusChange} label="Status">
                  <MenuItem value="interested">Interested</MenuItem>
                  <MenuItem value="done">Done</MenuItem>
                </Select>
              </FormControl>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNoteModal}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNote}>
            Save Note
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteNoteInfo} onClose={() => setDeleteNoteInfo(null)}>
        <DialogTitle>Delete Note?</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>Are you sure you want to delete this note? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteNoteInfo(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteNote}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TripDetails;
